import { useState, useCallback, useMemo, useEffect } from 'react'
import { sumBy } from 'lodash'
import { Flex, Text, TextField, Select, IconButton } from '@radix-ui/themes'
import { ChevronLeftIcon, ChevronRightIcon, TrashIcon } from '@radix-ui/react-icons'
import { formatBarcode, BarcodeFormat, BarcodeFormatsConfig } from './claim-form-constants'

const PRIZE_DIGIT_CAP = 12

type TicketEntry = {
  prizeAmount: unknown
  gameType: string
  barcodeNumber: string
}

export type EditableTicketsData = {
  tickets: TicketEntry[]
  paymentMethod: string
}

type EditableTicketFieldsProps = {
  data: EditableTicketsData
  schema: Record<string, unknown>
  onChange: (updated: EditableTicketsData) => void
  currentIndex?: number
  onIndexChange?: (index: number) => void
}

export function EditableTicketFields({
  data,
  schema,
  onChange,
  currentIndex: controlledIndex,
  onIndexChange,
}: EditableTicketFieldsProps) {
  const tickets = data.tickets
  const paymentMethod = data.paymentMethod
  const [internalIndex, setInternalIndex] = useState(controlledIndex ?? 0)
  const currentIndex = controlledIndex ?? internalIndex
  const [jumpInput, setJumpInput] = useState(String(currentIndex + 1))
  const [formatIndices, setFormatIndices] = useState<Record<number, number>>({})
  const total = tickets.length

  const sectionSchema = schema?.properties as Record<string, unknown> | undefined
  const ticketsSchema = sectionSchema?.tickets as Record<string, unknown> | undefined
  const ticketItemsSchema = ticketsSchema?.items as Record<string, unknown> | undefined
  const ticketProperties = (ticketItemsSchema?.properties ?? {}) as Record<string, unknown>
  const ticketRequired = ((ticketItemsSchema?.required ?? []) as string[]) || []

  const topLevelProps = sectionSchema ?? {}
  const paymentMethodSchema = topLevelProps.paymentMethod as Record<string, unknown> | undefined
  const paymentMethodOptions = useMemo<string[]>(
    () => (paymentMethodSchema?.enum as string[]) ?? [],
    [paymentMethodSchema],
  )
  const hasPaymentMethod = paymentMethodSchema !== undefined
  const enumMaxPrizeAmount = paymentMethodSchema?.['x-enum-max-prize-amount'] as
    | Record<string, number>
    | undefined

  const totalPrizeAmount = useMemo(
    () => tickets.reduce((sum, t) => sum + (Number(t.prizeAmount) || 0), 0),
    [tickets],
  )

  const disabledPaymentMessages = useMemo(() => {
    if (!enumMaxPrizeAmount) {
      return []
    }
    return paymentMethodOptions
      .filter((opt) => {
        const max = enumMaxPrizeAmount[opt]
        return max !== undefined && totalPrizeAmount > max
      })
      .map(
        (opt) =>
          `${opt} is not available for total prizes over $${enumMaxPrizeAmount[opt].toLocaleString()}.`,
      )
  }, [enumMaxPrizeAmount, paymentMethodOptions, totalPrizeAmount])

  const gameTypeSchema = ticketProperties.gameType as Record<string, unknown> | undefined
  const gameTypeOptions: string[] = (gameTypeSchema?.enum as string[]) ?? []

  const hasGameType = 'gameType' in ticketProperties
  const hasPrizeAmount = 'prizeAmount' in ticketProperties
  const hasBarcodeNumber = 'barcodeNumber' in ticketProperties

  const barcodeFormats = (schema?.barcodeFormats ?? {}) as BarcodeFormatsConfig

  const isMultiFormat = (gameType: string) => Array.isArray(barcodeFormats[gameType])

  const getSingleFormat = (gameType: string): BarcodeFormat | undefined => {
    const fmt = barcodeFormats[gameType]
    if (!fmt || Array.isArray(fmt)) {
      return undefined
    }
    return fmt
  }

  const isGameTypeRequired = ticketRequired.includes('gameType')
  const isPrizeAmountRequired = ticketRequired.includes('prizeAmount')
  const isBarcodeRequired = ticketRequired.includes('barcodeNumber')

  const ticket = useMemo(
    () => (tickets[currentIndex] as Record<string, unknown>) ?? {},
    [tickets, currentIndex],
  )

  const selectedFormatIndex = formatIndices[currentIndex] ?? 0

  const goTo = useCallback(
    (index: number) => {
      const clamped = Math.max(0, Math.min(total - 1, index))
      if (onIndexChange) {
        onIndexChange(clamped)
      } else {
        setInternalIndex(clamped)
      }
      setJumpInput(String(clamped + 1))
    },
    [total, onIndexChange],
  )

  const updateTicketField = useCallback(
    (field: string, value: unknown) => {
      const updatedTickets = [...tickets] as TicketEntry[]
      updatedTickets[currentIndex] = { ...updatedTickets[currentIndex], [field]: value }
      onChange({ ...data, tickets: updatedTickets })
    },
    [tickets, currentIndex, onChange, data],
  )

  const updateTicketFields = useCallback(
    (fields: Record<string, unknown>) => {
      const updatedTickets = [...tickets] as TicketEntry[]
      updatedTickets[currentIndex] = { ...updatedTickets[currentIndex], ...fields }
      onChange({ ...data, tickets: updatedTickets })
    },
    [tickets, currentIndex, onChange, data],
  )

  const updatePaymentMethod = useCallback(
    (value: string) => {
      onChange({ ...data, paymentMethod: value })
    },
    [data, onChange],
  )

  const paymentMethodCap = enumMaxPrizeAmount?.[paymentMethod]

  const handlePrizeChange = useCallback(
    (rawInput: string) => {
      let cleaned = rawInput.replace(/[^0-9.]/g, '')
      const firstDot = cleaned.indexOf('.')
      if (firstDot !== -1) {
        cleaned = cleaned.slice(0, firstDot + 1) + cleaned.slice(firstDot + 1).replace(/\./g, '')
        if (cleaned.length - firstDot - 1 > 2) {
          cleaned = cleaned.slice(0, firstDot + 3)
        }
      }
      const digitCount = cleaned.replace(/\./g, '').length
      if (digitCount > PRIZE_DIGIT_CAP) {
        return
      }
      if (paymentMethodCap !== undefined && cleaned !== '' && cleaned !== '.') {
        const numeric = Number(cleaned)
        if (Number.isFinite(numeric)) {
          const otherTotal = sumBy(
            tickets.filter((_, i) => i !== currentIndex),
            (t) => Number(t.prizeAmount) || 0,
          )
          const remaining = paymentMethodCap - otherTotal
          if (numeric > remaining) {
            cleaned = String(Math.max(0, remaining))
          }
        }
      }
      updateTicketField('prizeAmount', cleaned === '' ? undefined : Number(cleaned))
    },
    [paymentMethodCap, tickets, currentIndex, updateTicketField],
  )

  useEffect(() => {
    if (!enumMaxPrizeAmount || !paymentMethod) {
      return
    }
    const max = enumMaxPrizeAmount[paymentMethod]
    if (max !== undefined && totalPrizeAmount > max) {
      updatePaymentMethod('')
    }
  }, [totalPrizeAmount, paymentMethod, enumMaxPrizeAmount, updatePaymentMethod])

  const handleTicketCountChange = useCallback(
    (newCount: number) => {
      if (Number.isNaN(newCount) || newCount < 1) {
        return
      }
      if (newCount > total) {
        const blank: TicketEntry = { prizeAmount: 0, gameType: '', barcodeNumber: '' }
        const added = Array.from({ length: newCount - total }, () => ({ ...blank }))
        onChange({ ...data, tickets: [...tickets, ...added] })
      } else if (newCount < total) {
        onChange({ ...data, tickets: tickets.slice(0, newCount) })
        if (currentIndex >= newCount) {
          const clampedIndex = newCount - 1
          if (onIndexChange) {
            onIndexChange(clampedIndex)
          } else {
            setInternalIndex(clampedIndex)
          }
          setJumpInput(String(clampedIndex + 1))
        }
      }
    },
    [tickets, total, currentIndex, onChange, onIndexChange, data],
  )

  const removeTicket = useCallback(() => {
    const updatedTickets = tickets.filter((_, i) => i !== currentIndex)
    onChange({ ...data, tickets: updatedTickets })
    setFormatIndices((prev) => {
      const next: Record<number, number> = {}
      Object.entries(prev).forEach(([k, v]) => {
        const ki = Number(k)
        if (ki < currentIndex) {
          next[ki] = v
        } else if (ki > currentIndex) {
          next[ki - 1] = v
        }
      })
      return next
    })
    goTo(Math.min(currentIndex, tickets.length - 2))
  }, [tickets, currentIndex, onChange, goTo, data])

  const gameType = (ticket.gameType as string | undefined) ?? ''
  const multiFormats = isMultiFormat(gameType)
    ? (barcodeFormats[gameType] as BarcodeFormat[])
    : null

  return (
    <Flex direction="column" gap="3">
      <div className="claim-form-ticket-summary">
        <Flex direction="column" gap="1" className="claim-form-field">
          <Text size="2" color="gray">
            Number of Tickets
          </Text>
          <TextField.Root
            size="2"
            type="number"
            style={{ width: '80px' }}
            value={total}
            min={1}
            onChange={(e) => handleTicketCountChange(Number.parseInt(e.target.value, 10))}
          />
        </Flex>
        {hasPaymentMethod && (
          <Flex direction="column" gap="1" className="claim-form-field">
            <Text size="2" color="gray">
              Payment Method
            </Text>
            {paymentMethodOptions.length > 0 ? (
              <Select.Root size="2" value={paymentMethod} onValueChange={updatePaymentMethod}>
                <Select.Trigger placeholder="Select..." style={{ width: '220px' }} />
                <Select.Content>
                  {paymentMethodOptions.map((opt) => {
                    const max = enumMaxPrizeAmount?.[opt]
                    const disabled = max !== undefined && totalPrizeAmount > max
                    return (
                      <Select.Item key={opt} value={opt} disabled={disabled}>
                        {opt}
                      </Select.Item>
                    )
                  })}
                </Select.Content>
              </Select.Root>
            ) : (
              <TextField.Root
                size="2"
                value={paymentMethod}
                onChange={(e) => updatePaymentMethod(e.target.value)}
              />
            )}
            {disabledPaymentMessages.map((msg) => (
              <Text key={msg} size="2" color="red">
                {msg}
              </Text>
            ))}
          </Flex>
        )}
      </div>

      <Flex align="center" gap="2">
        <Text size="2" weight="bold">
          Ticket {currentIndex + 1} of {total}
        </Text>
        {total > 1 && (
          <IconButton
            size="2"
            variant="ghost"
            color="red"
            onClick={removeTicket}
            aria-label="Delete ticket">
            <TrashIcon />
          </IconButton>
        )}
      </Flex>

      <div className="claim-form-three-col">
        {hasPrizeAmount && (
          <Flex direction="column" gap="1" className="claim-form-field">
            <Text size="2" color="gray">
              Prize Amount {isPrizeAmountRequired && '*'}
            </Text>
            <TextField.Root
              size="2"
              type="text"
              inputMode="decimal"
              value={(ticket.prizeAmount as string | number | undefined)?.toString() ?? ''}
              onChange={(e) => handlePrizeChange(e.target.value)}
              placeholder="600">
              <TextField.Slot>$</TextField.Slot>
            </TextField.Root>
          </Flex>
        )}
        {hasGameType && (
          <Flex direction="column" gap="1" className="claim-form-field">
            <Text size="2" color="gray">
              Game Type {isGameTypeRequired && '*'}
            </Text>
            {gameTypeOptions.length > 0 ? (
              <Select.Root
                size="2"
                value={gameType}
                onValueChange={(v) => {
                  updateTicketFields({ gameType: v, barcodeNumber: '' })
                  setFormatIndices((prev) => ({ ...prev, [currentIndex]: 0 }))
                }}>
                <Select.Trigger placeholder="Select..." />
                <Select.Content>
                  {gameTypeOptions.map((opt) => (
                    <Select.Item key={opt} value={opt}>
                      {opt}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
            ) : (
              <TextField.Root
                size="2"
                value={gameType}
                onChange={(e) => {
                  updateTicketFields({ gameType: e.target.value, barcodeNumber: '' })
                  setFormatIndices((prev) => ({ ...prev, [currentIndex]: 0 }))
                }}
              />
            )}
          </Flex>
        )}
        {hasBarcodeNumber && (!hasGameType || !!ticket.gameType) && (
          <Flex direction="column" gap="1" className="claim-form-field">
            <Text size="2" color="gray">
              Barcode Number {isBarcodeRequired && '*'}
            </Text>
            {multiFormats ? (
              <Flex direction="column" gap="1">
                {multiFormats.map((fmt, fIdx) => {
                  const isSelected = selectedFormatIndex === fIdx
                  return (
                    <Flex key={fIdx} align="center" gap="2">
                      <input
                        type="radio"
                        name={`barcode-format-${currentIndex}`}
                        checked={isSelected}
                        onChange={() => {
                          setFormatIndices((prev) => ({ ...prev, [currentIndex]: fIdx }))
                          updateTicketField('barcodeNumber', '')
                        }}
                      />
                      {isSelected ? (
                        <TextField.Root
                          size="2"
                          style={{ flex: 1 }}
                          value={(ticket.barcodeNumber as string | undefined) ?? ''}
                          placeholder={fmt.placeholder ?? 'Enter barcode'}
                          maxLength={fmt.placeholder?.length}
                          onChange={(e) => {
                            const formatted = fmt.placeholder
                              ? formatBarcode(e.target.value, fmt.placeholder)
                              : e.target.value
                            updateTicketField('barcodeNumber', formatted)
                          }}
                        />
                      ) : (
                        <Text size="2" color="gray">
                          {fmt.placeholder}
                        </Text>
                      )}
                    </Flex>
                  )
                })}
              </Flex>
            ) : (
              <TextField.Root
                size="2"
                value={String((ticket.barcodeNumber as string) ?? '')}
                onChange={(e) => {
                  const format = getSingleFormat(gameType)
                  const formatted = format?.placeholder
                    ? formatBarcode(e.target.value, format.placeholder)
                    : e.target.value
                  updateTicketField('barcodeNumber', formatted)
                }}
                maxLength={getSingleFormat(gameType)?.placeholder?.length}
                placeholder={getSingleFormat(gameType)?.placeholder ?? 'Enter barcode'}
              />
            )}
          </Flex>
        )}
      </div>

      {total > 1 && (
        <Flex direction="column" align="center" gap="1" className="claim-form-ticket-nav">
          <Text size="2" color="gray">
            Jump to Ticket
          </Text>
          <Flex align="center" gap="2">
            <IconButton
              size="2"
              variant="soft"
              color="gray"
              disabled={currentIndex === 0}
              onClick={() => goTo(currentIndex - 1)}>
              <ChevronLeftIcon />
            </IconButton>
            <input
              className="claim-form-ticket-jump-input"
              value={jumpInput}
              onChange={(e) => setJumpInput(e.target.value)}
              onBlur={() => {
                const num = Number.parseInt(jumpInput, 10)
                if (Number.isNaN(num)) {
                  setJumpInput(String(currentIndex + 1))
                } else {
                  goTo(num - 1)
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.currentTarget.blur()
                }
              }}
            />
            <Text size="2" color="gray">
              of {total}
            </Text>
            <IconButton
              size="2"
              variant="soft"
              color="gray"
              disabled={currentIndex === total - 1}
              onClick={() => goTo(currentIndex + 1)}>
              <ChevronRightIcon />
            </IconButton>
          </Flex>
        </Flex>
      )}
    </Flex>
  )
}
